import type { ListingsBlockProps, Listing } from '../types';
import { theme, sectionColors, cssColor, cardGridClass } from '../theme';
import { listingsShowOf, listingsWhoseOf, LISTINGS_SHOW_LABELS, type ListingsShow } from '../listingsFeed';

/** The badge on each card for a filtered section (none on Featured, as before). */
const CARD_BADGE: Record<ListingsShow, string | null> = {
  featured: null,
  just_listed: 'Just Listed',
  just_reduced: 'Price Reduced',
  recently_sold: 'Sold',
  under_contract: 'Under Contract',
  open_houses: 'Open House',
};

function formatPrice(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

/**
 * Pure listings grid. The renderer is "dumb": it receives `listings` from the host
 * (tenant-sites reads listings_cache server-side; builder hydrates from an API) and
 * NEVER fetches. Renders a graceful empty state rather than throwing.
 *
 * `isSample` (Wave-A F7): the host sets this when `listings` came from the sample-seed
 * fallback (no MLS connected / connection failed), never the agent's real inventory —
 * labeled here instead of presented as if it were live, matching the "never claim
 * connected when it's sample" rule elsewhere in the AI surfaces (build recap, chat).
 */
export function ListingsBlock({ props, listings, isSample, previewMode }: { props: ListingsBlockProps; listings: Listing[]; isSample?: boolean; previewMode?: boolean }) {
  const { heading, limit, layout = 'grid' } = props;
  const items = typeof limit === 'number' ? listings.slice(0, limit) : listings;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  const cardBg = cssColor(props.cardBgColor);
  const priceColor = cssColor(props.priceColor);
  const show = listingsShowOf(props);
  const badge = CARD_BADGE[show];

  const officeOnly = listingsWhoseOf(props) === 'office_only';

  if (items.length === 0 && (show !== 'featured' || officeOnly)) {
    // A filtered section with nothing in it (no price cuts this month) is left off the live page
    // rather than showing an empty heading; the Studio says why it is not there.
    if (!previewMode) return null;
    return (
      <section className="px-6 py-12 text-center" style={{ ...colors, fontFamily: theme.bodyFont }}>
        {heading ? <h2 className="mx-auto mb-4 max-w-6xl text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2> : null}
        <p className="opacity-60">
          {officeOnly
            ? <>Your office has no homes for “{LISTINGS_SHOW_LABELS[show]}” right now, so this section is hidden on your live site until it does.</>
            : <>Nothing to show for “{LISTINGS_SHOW_LABELS[show]}” right now, so this section is hidden on your live site until there is.</>}
        </p>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="px-6 py-12 text-center" style={{ ...colors, fontFamily: theme.bodyFont }}>
        <p className="opacity-60">No listings available right now.</p>
      </section>
    );
  }

  const grid = cardGridClass({ columns: props.columns, layout });
  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      {heading ? <h2 className={`mx-auto max-w-6xl text-2xl font-semibold ${isSample ? 'mb-2' : 'mb-8'}`} style={{ fontFamily: theme.headingFont }}>{heading}</h2> : null}
      {isSample ? (
        <p className="mx-auto mb-8 max-w-6xl text-sm opacity-60">
          Sample listings — connect an MLS to show your real inventory here.
        </p>
      ) : null}
      <div className={`mx-auto grid max-w-6xl gap-6 ${grid}`}>
        {items.map((l) => (
          // Each card links to the listing detail page (/listings/:key) so the
          // detail flow works end-to-end on the live site.
          <a
            key={l.listing_key}
            href={`/listings/${encodeURIComponent(l.listing_key)}`}
            className="tmpl-card blk-card group block overflow-hidden rounded-lg border border-black/10 transition hover:shadow-md"
            style={cardBg ? { backgroundColor: cardBg } : undefined}
          >
            <div className="tmpl-card-media relative overflow-hidden">
              {l.photos?.[0] ? (
                // Slow image zoom on hover — a premium real-estate touch that composes
                // with the per-look card styling (additive transform only).
                <img src={l.photos[0]} alt={l.address.street} loading="lazy" decoding="async" className="tmpl-card-img h-48 w-full object-cover transition-transform duration-500 ease-out will-change-transform group-hover:scale-105" />
              ) : null}
              {badge && l.photos?.[0] ? (
                <span className="blk-listing-badge absolute left-3 top-3 rounded-full bg-black/75 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                  {badge}
                </span>
              ) : null}
              {/* Hover-reveal overlay — shown only for the 'overlay' card style (CSS in the host app). */}
              <div className="tmpl-card-reveal hidden" aria-hidden="true">
                <span className="tmpl-card-reveal-status">{badge ?? 'For Sale'}</span>
                <ul className="tmpl-card-reveal-facts">
                  {l.beds != null ? <li>{l.beds} Bedrooms</li> : null}
                  {l.baths_total != null ? <li>{l.baths_total} Bathrooms</li> : null}
                  {l.sqft != null ? <li>{l.sqft.toLocaleString()} Sq Ft</li> : null}
                </ul>
                <span className="tmpl-card-reveal-cta">View Details</span>
              </div>
            </div>
            <div className="p-4">
              {/* No photo: the badge goes above the price instead of over the (empty) image. */}
              {badge && !l.photos?.[0] ? (
                <span className="blk-listing-badge mb-2 inline-block rounded-full bg-black/75 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                  {badge}
                </span>
              ) : null}
              <div className="text-xl font-semibold tabular-nums" style={priceColor ? { color: priceColor } : undefined}>
                {show === 'recently_sold' && l.sold_price ? formatPrice(l.sold_price) : formatPrice(l.list_price)}
                {show === 'just_reduced' && l.original_price && l.original_price > l.list_price ? (
                  <span className="ml-2 text-sm font-normal line-through opacity-60">{formatPrice(l.original_price)}</span>
                ) : null}
              </div>
              <div className="text-sm">{l.address.street}</div>
              <div className="text-sm opacity-70">
                {l.address.city}, {l.address.state} {l.address.zip}
              </div>
              <div className="mt-2 text-sm opacity-70">
                {l.beds} bd · {l.baths_total} ba{l.sqft ? ` · ${l.sqft.toLocaleString()} sqft` : ''}
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
